import React, {useCallback, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Chip, FormControlLabel, InputAdornment, Stack, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {AbcSharp, Close, Edit, Loop, Percent, PinSharp, Search, ToggleOff} from "@mui/icons-material";
import {FeatureFlagAdminService} from "../services/feature-flag-service";
import {FeatureFlagAdminController$FeatureFlagJson} from "../api/generated/featureflags-rest";
import {useTranslation} from "react-i18next";
import {Widget} from "../components/Common";
import {LoadingFallback} from "../components/Common/loading-fallback";
import {ColumnDefinition, DataTable} from "../components/Table/data-table";

type FlagType = "bool" | "long" | "double" | "string";

const flagTypeLabels = {
    bool: "Bool",
    long: "Long",
    double: "Double",
    string: "String",
} as const;

const flagTypeIcons = {
    bool: <ToggleOff fontSize="small"/>,
    long: <PinSharp fontSize="small"/>,
    double: <Percent fontSize="small"/>,
    string: <AbcSharp fontSize="small"/>,
} as const;

const inferType = (value: unknown): FlagType => {
    switch (typeof value) {
        case "boolean": return "bool";
        case "number": return Number.isInteger(value) ? "long" : "double";
        default: return "string";
    }
};

const isOverridden = (flag: FeatureFlagAdminController$FeatureFlagJson) => flag.overrideValue !== null && flag.overrideValue !== undefined;

const getCurrentValue = (flag: FeatureFlagAdminController$FeatureFlagJson) => isOverridden(flag) ? flag.overrideValue : flag.defaultValue;

const formatValue = (value: unknown) => {
    if (value == null) {
        return "---";
    }
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    return String(value);
};

const parseFlagValue = (flagType: FlagType, input: string): unknown => {
    switch (flagType) {
        case "bool": return input.toLowerCase() === "true";
        case "long": return Number.parseInt(input, 10);
        case "double": return Number.parseFloat(input);
        default: return input;
    }
}

function ValueChip({flag, overridden, currentValue, onEdit}: {
    flag: FeatureFlagAdminController$FeatureFlagJson;
    overridden: boolean;
    currentValue: string;
    onEdit: () => void;
}) {
    const queryClient = useQueryClient();
    const resetMutation = useMutation({
        mutationFn: (key: string) => FeatureFlagAdminService.reset(key),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["admin", "feature-flags"]}),
    });

    if (overridden) {
        return (
            <Tooltip title={formatValue(flag.defaultValue)}>
                <Chip
                    label={currentValue}
                    color="primary"
                    size="small"
                    disabled={resetMutation.isPending}
                    deleteIcon={resetMutation.isPending ? <Loop fontSize="small" /> : <Close fontSize="small" />}
                    onDelete={e => {
                        e.stopPropagation();
                        resetMutation.mutate(flag.key);
                    }}
                />
            </Tooltip>
        );
    }
    return (
        <Chip
            label={currentValue}
            color="default"
            size="small"
            deleteIcon={<Edit fontSize="small" />}
            onDelete={onEdit}
        />
    );
}

export function AdminFeatureFlagsPage() {
    const {t} = useTranslation();
    const queryClient = useQueryClient();

    const {data: flags = [], isLoading} = useQuery({
        queryKey: ["admin", "feature-flags"],
        queryFn: () => FeatureFlagAdminService.list().then(result =>
            [...result].sort((a, b) => a.key.localeCompare(b.key))
        ),
    });

    const [filters, setFilters] = useState<{search: string; flagTypes: FlagType[]; showOverriddenOnly: boolean}>({
        search: "",
        flagTypes: ["bool", "long", "double", "string"],
        showOverriddenOnly: false,
    });

    const updateMutation = useMutation({
        mutationFn: ({key, value}: { key: string; value: Object }) =>
            FeatureFlagAdminService.update(key, value),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["admin", "feature-flags"]}),
    });

    const handleEditValue = useCallback((flag: FeatureFlagAdminController$FeatureFlagJson) => {
        const flagType = inferType(flag.defaultValue);
        const input = window.prompt(t("admin.featureFlags.edit.prompt", {type: flagTypeLabels[flagType]}), formatValue(getCurrentValue(flag)));
        if (input === null) {
            return;
        }
        const parsed = parseFlagValue(flagType, input);

        if (typeof parsed === "number" && Number.isNaN(parsed)) {
            return;
        }
        updateMutation.mutate({key: flag.key, value: parsed as Object});
    }, [updateMutation, t]);

    const rows = useMemo(() => {
        let result = flags.map(flag => ({...flag, id: flag.key}));

        if (filters.search) {
            const s = filters.search.toLowerCase();
            result = result.filter(f => {
                const type = inferType(f.defaultValue).toLowerCase();
                const def = formatValue(f.defaultValue).toLowerCase();
                const cur = formatValue(getCurrentValue(f)).toLowerCase();
                return f.key.toLowerCase().includes(s) || def.includes(s) || cur.includes(s) || type.includes(s);
            });
        }

        if (filters.flagTypes.length < 4) {
            result = result.filter(f => filters.flagTypes.includes(inferType(f.defaultValue)));
        }

        if (filters.showOverriddenOnly) {
            result = result.filter(isOverridden);
        }

        return result;
    }, [flags, filters]);

    if (isLoading) {
        return <LoadingFallback />;
    }

    type Row = FeatureFlagAdminController$FeatureFlagJson & {id: string};
    const columns: ColumnDefinition<Row>[] = [
        {
            id: "type",
            accessor: row => inferType(row.defaultValue),
            header: t("admin.featureFlags.columns.type"),
            width: "1%",
            render: value => {
                const type = value as FlagType;
                return (
                    <Chip label={flagTypeLabels[type]}
                          icon={flagTypeIcons[type]}
                          size="small"
                          sx={{minWidth: 60}}/>
                );
            },
        },
        {
            id: "key",
            accessor: flag => flag.key,
            header: t("admin.featureFlags.columns.key"),
            sortBy: value => (value as string).toLowerCase(),
            render: (value, row) => {
                const flag = row;
                const overridden = isOverridden(flag);
                return (
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{fontFamily: "monospace", cursor: "pointer", fontWeight: overridden ? "bold" : "normal"}}
                        onClick={() => handleEditValue(flag)}
                    >
                        {value as string}
                    </Typography>
                );
            },
        },
        {
            id: "value",
            accessor: row => row,
            header: t("admin.featureFlags.columns.value"),
            textAlign: "right",
            sortBy: value => formatValue(getCurrentValue(value as Row)).toLowerCase(),
            render: value => {
                const flag = value as Row;
                const overridden = isOverridden(flag);
                const currentValue = formatValue(getCurrentValue(flag));
                return (
                    <ValueChip flag={flag}
                               overridden={overridden}
                               currentValue={currentValue}
                               onEdit={() => handleEditValue(flag)}/>
                );
            },
        },
    ];

    return (
        <Stack gap={2} padding={3}>
            <Widget displayName={t("admin.featureFlags.title")}>
                <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
                    <TextField
                        label={t("admin.featureFlags.filter.search")}
                        variant="outlined"
                        size="small"
                        value={filters.search}
                        onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{minWidth: 250}}
                    />
                    <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                        {Object.entries(flagTypeLabels).map(([key, label]) => {
                            const type = key as FlagType;
                            const selected = filters.flagTypes.includes(type);
                            return (
                                <Chip
                                    key={key}
                                    label={label}
                                    icon={flagTypeIcons[type]}
                                    color={selected ? "primary" : "default"}
                                    variant={selected ? "filled" : "outlined"}
                                    size="small"
                                    onClick={() =>
                                        setFilters(f => ({
                                            ...f,
                                            flagTypes: selected
                                                ? f.flagTypes.filter(t => t !== type)
                                                : [...f.flagTypes, type],
                                        }))
                                    }
                                />
                            );
                        })}
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={filters.showOverriddenOnly}
                                onChange={e =>
                                    setFilters(f => ({...f, showOverriddenOnly: e.target.checked}))
                                }
                                size="small"
                            />
                        }
                        label={t("admin.featureFlags.filter.overriddenOnly")}
                    />
                </Stack>

                {!rows.length ? (
                    <Typography align="center" color="text.secondary" sx={{py: 3}}>
                        {t("admin.featureFlags.noFlagsFound")}
                    </Typography>
                ) : (
                    <DataTable data={rows} columns={columns} size="small" />
                )}
            </Widget>
        </Stack>
    );
}
